import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody } from "@/components/ui/dialog";
import { Globe, Users, Lock, Check, Loader2, MessageSquareLock } from "lucide-react";
import { toast } from "sonner";

const OPTIONS = [
  { v: "everyone", Icon: Globe, label: ["privacy.dm_everyone", "Tout le monde"], hint: ["privacy.dm_everyone_hint", "Les messages des membres qui ne te suivent pas arrivent dans « Demandes »."] },
  { v: "mutual", Icon: Users, label: ["privacy.dm_mutual", "Abonnés mutuels"], hint: ["privacy.dm_mutual_hint", "Seules les personnes que tu suis et qui te suivent peuvent t'écrire."] },
  { v: "nobody", Icon: Lock, label: ["privacy.dm_nobody", "Personne"], hint: ["privacy.dm_nobody_hint", "Personne ne peut démarrer une nouvelle conversation avec toi."] },
];

// Réglage membre « Qui peut m'écrire » (GET/PATCH /chat/privacy) — liste de 3 choix.
export const DmPrivacyOptions = () => {
  const { t } = useApp();
  const [value, setValue] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/chat/privacy").then(({ data }) => setValue(data.dm_privacy || "everyone")).catch(() => setValue("everyone")); }, []);
  const pick = async (v) => {
    if (v === value || busy) return;
    setBusy(v);
    try { await api.patch("/chat/privacy", { dm_privacy: v }); setValue(v); toast.success(t("privacy.saved", "Réglage enregistré")); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-2" data-testid="dm-privacy-options" data-value={value || ""}>
      {OPTIONS.map(({ v, Icon, label, hint }) => {
        const on = value === v;
        return (
          <button key={v} onClick={() => pick(v)} disabled={value === null}
            className="w-full flex items-start gap-3 rounded-xl border p-3 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]"
            style={{ borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)", background: on ? "var(--yalola-primary-soft, #fff7ed)" : undefined }}
            data-testid={`dm-privacy-${v}`} data-on={on ? "1" : "0"}>
            <Icon size={18} className="mt-0.5 shrink-0" style={{ color: on ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold" style={{ color: "var(--yalola-text)" }}>{t(label[0], label[1])}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{t(hint[0], hint[1])}</div>
            </div>
            {busy === v ? <Loader2 size={16} className="animate-spin shrink-0" /> : on && <Check size={16} className="shrink-0" style={{ color: "var(--yalola-primary)" }} />}
          </button>
        );
      })}
    </div>
  );
};

// Carte pour la page Réglages (section Confidentialité).
export const DmPrivacyCard = () => {
  const { t } = useApp();
  return (
    <div className="yalola-card p-5" id="messagerie" data-testid="settings-dm-privacy-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareLock size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">{t("privacy.dm_title", "Qui peut m'écrire")}</div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--yalola-text-muted)" }}>{t("privacy.dm_desc", "Choisis qui peut démarrer une conversation privée avec toi. Les conversations déjà acceptées continuent quoi qu'il arrive.")}</p>
      <DmPrivacyOptions />
    </div>
  );
};

// Raccourci depuis la Messagerie (icône ⚙ en haut).
export const DmPrivacyDialog = ({ open, onOpenChange }) => {
  const { t } = useApp();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="sheet" className="sm:max-w-md" data-testid="dm-privacy-dialog">
        <DialogHeader>
          <DialogTitle>{t("privacy.dm_title", "Qui peut m'écrire")}</DialogTitle>
          <DialogDescription>{t("privacy.dm_desc", "Choisis qui peut démarrer une conversation privée avec toi. Les conversations déjà acceptées continuent quoi qu'il arrive.")}</DialogDescription>
        </DialogHeader>
        <DialogBody>{open && <DmPrivacyOptions />}</DialogBody>
      </DialogContent>
    </Dialog>
  );
};
